import { useCallback, useMemo, useState } from 'react';
import { noop } from '@cbhq/cds-utils';
import { Button } from '@cbhq/cds-web/buttons';
import { ListCell } from '@cbhq/cds-web/cells';
import { Switch } from '@cbhq/cds-web/controls';
import { Icon } from '@cbhq/cds-web/icons';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader, Tooltip } from '@cbhq/cds-web/overlays';
import { TextBody } from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { CoinbaseWebhook } from ':cloud/types/ts_types';
import { truncateMiddle } from ':cloud/utils/common';
import { ClipboardIcon } from ':cloud/widgets/sharedcomponents';
import { CoinbaseWebhookEditFieldsModal } from ':cloud/widgets/webhooks/CoinbaseWebhookEditFieldsModal';
import { WebhookFiltersTitle } from ':cloud/widgets/webhooks/components/WebhookFiltersTitle';
import { WEBHOOK_STATUS } from ':cloud/widgets/webhooks/constants';
import {
  getAddressEntity,
  getAddressesTextFromWebhookFilter,
} from ':cloud/widgets/webhooks/helpers';

import { getEventType, getEventTypeProps, getNetwork, getStatus } from '../helpers';

type WebhookEditModalProps = {
  onRequestClose?: () => void;
  onSubmit: (webhook: CoinbaseWebhook) => void;
  onDelete: (webhook: CoinbaseWebhook) => void;
  onToggleActivate: (webhook: CoinbaseWebhook) => void;
  onSuccess?: () => void;
  webhook: CoinbaseWebhook;
};

export function CoinbaseWebhookEditModal({
  onRequestClose,
  onSubmit,
  onDelete,
  onToggleActivate,
  webhook,
}: WebhookEditModalProps) {
  const [isEditFieldsModalVisible, setIsEditFieldsModalVisible] = useState(false);
  const isActive = !webhook.webhookStatus || webhook.webhookStatus === WEBHOOK_STATUS.active;
  const { isPhone } = useSimpleBreakpoints();

  const { isWalletActivity, isSmartContractEvents } = getEventTypeProps(webhook.eventType);

  const handleEditButtonPress = useCallback(() => {
    setIsEditFieldsModalVisible(true);
  }, []);

  const handleEditFieldsModalClose = useCallback(() => {
    setIsEditFieldsModalVisible(false);
  }, []);

  const handleEditFieldsSubmit = useCallback(
    (updatedWebhook: CoinbaseWebhook) => {
      onSubmit(updatedWebhook);
      setIsEditFieldsModalVisible(false);
      onRequestClose?.();
    },
    [onSubmit, onRequestClose],
  );

  const handleDelete = useCallback(() => {
    onDelete(webhook);
    onRequestClose?.();
  }, [onDelete, webhook, onRequestClose]);

  const handleToggleWebhookStatus = useCallback(() => {
    const updatedWebhook: CoinbaseWebhook = {
      ...webhook,
      webhookStatus: isActive ? WEBHOOK_STATUS.deactivated : WEBHOOK_STATUS.active,
    };

    onToggleActivate(updatedWebhook);
  }, [webhook, isActive, onToggleActivate]);

  const filtersContent = useMemo(() => {
    if (isWalletActivity || isSmartContractEvents) {
      const walletId = webhook?.eventTypeFilter?.walletActivityFilter?.walletId;
      const title = `${getAddressEntity(isSmartContractEvents)} Addresses`;
      const walledIdTitleComponent = (
        <HStack alignItems="center" gap={0.5}>
          Wallet ID
          <Tooltip content="Wallet ID is added when creating a webhook through our SDK.">
            <Icon name="info" size="s" color="foregroundMuted" />
          </Tooltip>
        </HStack>
      );

      return (
        <>
          <ListCell
            title={title}
            description={getAddressesTextFromWebhookFilter(webhook?.eventTypeFilter, '\n')}
            multiline
          />
          {walletId && <ListCell description={walledIdTitleComponent} detail={walletId} />}
        </>
      );
    }
    return (
      <>
        <ListCell
          description="Contract Address"
          detail={webhook.eventFilters?.[0].contractAddress}
        />
        <ListCell description="From Address" detail={webhook.eventFilters?.[0].fromAddress} />
        <ListCell description="To Address" detail={webhook.eventFilters?.[0].toAddress} />
      </>
    );
  }, [webhook.eventFilters, isWalletActivity, webhook?.eventTypeFilter, isSmartContractEvents]);

  return (
    <Modal onRequestClose={onRequestClose || noop} visible width={isPhone ? undefined : 480}>
      {isEditFieldsModalVisible ? (
        <CoinbaseWebhookEditFieldsModal
          webhook={webhook}
          onRequestClose={handleEditFieldsModalClose}
          onSubmit={handleEditFieldsSubmit}
        />
      ) : (
        <>
          <ModalHeader title="Webhook Details" />
          <ModalBody>
            <VStack as="ul" gap={2}>
              <ListCell as="li" description="Webhook UUID" detail={webhook.uuid?.hex} />
              <ListCell
                as="li"
                description="Webhook URL"
                action={
                  <HStack alignItems="center">
                    <VStack background="primaryWash" spacingHorizontal={1}>
                      <TextBody as="p">
                        {truncateMiddle(webhook.notificationUri, 12, 12, '...')}
                      </TextBody>
                    </VStack>
                    <ClipboardIcon text={webhook.notificationUri} iconSize="s" stopPropagation />
                  </HStack>
                }
              />
              <ListCell as="li" description="Network Type" detail={getNetwork(webhook.network)} />
              <ListCell as="li" description="Event Type" detail={getEventType(webhook.eventType)} />
              <ListCell
                as="li"
                description="Status"
                action={
                  <HStack justifyContent="flex-end">
                    <Switch onChange={handleToggleWebhookStatus} checked={isActive}>
                      {getStatus(webhook.webhookStatus)}
                    </Switch>
                  </HStack>
                }
              />
              <WebhookFiltersTitle spacingStart={3} />
              {filtersContent}
            </VStack>
          </ModalBody>
          <ModalFooter
            primaryAction={
              <Button variant="negative" onPress={handleDelete}>
                Delete
              </Button>
            }
            secondaryAction={
              <Button variant="secondary" onPress={handleEditButtonPress}>
                Edit
              </Button>
            }
          />
        </>
      )}
    </Modal>
  );
}
